import React, { useState } from 'react';
import { 
  Users, 
  TrendingUp, 
  Target, 
  BarChart3,
  Heart,
  DollarSign,
  Clock,
  MessageCircle,
  Star,
  UserPlus,
  UserMinus,
  Filter,
  Search,
  MapPin,
  PieChart,
  Activity,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  Smile,
  Frown,
  Meh,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Lightbulb
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';

const CustomerDashboard: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('monthly');
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [selectedView, setSelectedView] = useState('overview');

  // Key customer metrics
  const customerMetrics = [
    {
      id: 'total_customers',
      name: 'Total Customers',
      value: 24850,
      change: 5.2,
      positive: true,
      description: 'Total active customer base',
      target: 25000,
      unit: '',
      icon: Users
    },
    {
      id: 'new_customers',
      name: 'New Customers',
      value: 1245,
      change: 12.8,
      positive: true,
      description: 'New customers this month',
      target: 1000,
      unit: '',
      icon: UserPlus
    },
    {
      id: 'customer_ltv',
      name: 'Customer LTV',
      value: 420,
      change: 8.7,
      positive: true,
      description: 'Average lifetime value',
      target: 400,
      unit: '£',
      icon: DollarSign
    },
    {
      id: 'nps_score',
      name: 'NPS Score',
      value: 72,
      change: 8.0,
      positive: true,
      description: 'Net Promoter Score',
      target: 70,
      unit: '',
      icon: Star
    },
    {
      id: 'csat_score',
      name: 'CSAT Score',
      value: 4.7,
      change: 0.3,
      positive: true,
      description: 'Customer Satisfaction Score',
      target: 4.5,
      unit: '/5',
      icon: Heart
    },
    {
      id: 'churn_rate',
      name: 'Churn Rate',
      value: 2.1,
      change: -0.5,
      positive: true,
      description: 'Monthly customer churn',
      target: 2.5,
      unit: '%',
      icon: UserMinus
    }
  ];

  // Customer acquisition data
  const acquisitionData = [
    { month: 'Jan', organic: 320, paid: 180, referral: 120, direct: 80, total: 700, cac: 28.5 },
    { month: 'Feb', organic: 350, paid: 200, referral: 140, direct: 90, total: 780, cac: 27.2 },
    { month: 'Mar', organic: 380, paid: 220, referral: 150, direct: 100, total: 850, cac: 26.8 },
    { month: 'Apr', organic: 420, paid: 250, referral: 170, direct: 110, total: 950, cac: 25.4 },
    { month: 'May', organic: 450, paid: 280, referral: 190, direct: 120, total: 1040, cac: 24.1 },
    { month: 'Jun', organic: 480, paid: 310, referral: 210, direct: 130, total: 1130, cac: 22.8 },
  ];

  // Customer retention data
  const retentionData = [
    { month: 'Jan', retention: 92, churn: 8, ltv: 380 },
    { month: 'Feb', retention: 93, churn: 7, ltv: 390 },
    { month: 'Mar', retention: 91, churn: 9, ltv: 385 },
    { month: 'Apr', retention: 94, churn: 6, ltv: 400 },
    { month: 'May', retention: 95, churn: 5, ltv: 410 },
    { month: 'Jun', retention: 96, churn: 4, ltv: 420 },
  ];

  // Customer sentiment data
  const sentimentData = [
    { category: 'Positive', value: 65, color: '#10B981' },
    { category: 'Neutral', value: 25, color: '#6B7280' },
    { category: 'Negative', value: 10, color: '#EF4444' },
  ];

  // Feedback themes data
  const feedbackThemesData = [
    { theme: 'User Interface', positive: 82, negative: 18, volume: 320 },
    { theme: 'Performance', positive: 75, negative: 25, volume: 280 },
    { theme: 'Features', positive: 68, negative: 32, volume: 420 },
    { theme: 'Support', positive: 90, negative: 10, volume: 180 },
    { theme: 'Pricing', positive: 60, negative: 40, volume: 150 },
  ];

  // Complaint data
  const complaintData = [
    { month: 'Jan', volume: 45, resolved: 42, avgTime: 8.2 },
    { month: 'Feb', volume: 52, resolved: 48, avgTime: 7.8 },
    { month: 'Mar', volume: 48, resolved: 45, avgTime: 7.5 },
    { month: 'Apr', volume: 42, resolved: 40, avgTime: 6.9 },
    { month: 'May', volume: 38, resolved: 37, avgTime: 6.2 },
    { month: 'Jun', volume: 35, resolved: 34, avgTime: 5.8 },
  ];

  // Demographics data
  const demographicsData = {
    age: [
      { name: '18-24', value: 15 },
      { name: '25-34', value: 32 },
      { name: '35-44', value: 28 },
      { name: '45-54', value: 18 },
      { name: '55+', value: 7 },
    ],
    gender: [
      { name: 'Male', value: 58 },
      { name: 'Female', value: 41 },
      { name: 'Other', value: 1 },
    ],
    location: [
      { name: 'North America', value: 42 },
      { name: 'Europe', value: 35 },
      { name: 'Asia Pacific', value: 15 },
      { name: 'Latin America', value: 5 },
      { name: 'Other', value: 3 },
    ]
  };

  // Customer personas
  const personaData = [
    {
      name: 'Enterprise Eva',
      description: 'Enterprise decision maker focused on scalability and security',
      traits: ['Security-focused', 'ROI-driven', 'Team collaboration', 'Enterprise integration'],
      percentage: 35,
      color: '#3B82F6'
    },
    {
      name: 'Startup Sam',
      description: 'Startup founder looking for quick wins and growth',
      traits: ['Growth-focused', 'Price-sensitive', 'Feature-hungry', 'Quick implementation'],
      percentage: 28,
      color: '#10B981'
    },
    {
      name: 'Technical Tina',
      description: 'Technical user who values deep functionality and API access',
      traits: ['API-first', 'Customization', 'Technical documentation', 'Integration capabilities'],
      percentage: 22,
      color: '#8B5CF6'
    },
    {
      name: 'Manager Mike',
      description: 'Team manager focused on productivity and team visibility',
      traits: ['Team oversight', 'Reporting', 'Process optimization', 'Ease of use'],
      percentage: 15,
      color: '#F59E0B'
    }
  ];

  // Customer journey stages
  const journeyData = [
    { name: 'Awareness', value: 100, conversion: 100 },
    { name: 'Consideration', value: 68, conversion: 68 },
    { name: 'Decision', value: 42, conversion: 62 },
    { name: 'Purchase', value: 28, conversion: 67 },
    { name: 'Retention', value: 22, conversion: 79 },
    { name: 'Advocacy', value: 12, conversion: 55 },
  ];

  // Channel performance data
  const channelData = [
    { channel: 'Organic Search', cac: 22.4, volume: 480, conversion: 3.2 },
    { channel: 'Paid Search', cac: 35.8, volume: 310, conversion: 2.8 },
    { channel: 'Social Media', cac: 28.5, volume: 210, conversion: 2.4 },
    { channel: 'Email', cac: 12.2, volume: 130, conversion: 4.5 },
    { channel: 'Referral', cac: 18.7, volume: 210, conversion: 5.2 },
    { channel: 'Direct', cac: 15.3, volume: 130, conversion: 3.8 },
  ];

  // Customer insights
  const customerInsights = [
    {
      id: 1,
      type: 'opportunity',
      title: 'Mobile App User Satisfaction Surge',
      description: 'Mobile app users showing 28% higher satisfaction scores than web users',
      impact: 'high',
      confidence: 92,
      action: 'Prioritize mobile feature development',
      timeframe: 'This month'
    },
    {
      id: 2,
      type: 'concern',
      title: 'Enterprise Segment Churn Risk',
      description: 'Early warning signals of increased churn in enterprise segment',
      impact: 'high',
      confidence: 78,
      action: 'Conduct enterprise customer interviews',
      timeframe: '2 weeks ago'
    },
    {
      id: 3,
      type: 'success',
      title: 'Referral Program Effectiveness',
      description: 'Referral channel showing lowest CAC and highest LTV',
      impact: 'medium',
      confidence: 95,
      action: 'Expand referral incentives',
      timeframe: '1 week ago'
    }
  ];

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'opportunity': return <ArrowUpRight className="w-5 h-5 text-green-500" />;
      case 'concern': return <ArrowDownRight className="w-5 h-5 text-red-500" />;
      case 'success': return <Star className="w-5 h-5 text-yellow-500" />;
      default: return <Activity className="w-5 h-5 text-blue-500" />;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'opportunity': return 'border-green-600 bg-green-900';
      case 'concern': return 'border-red-600 bg-red-900';
      case 'success': return 'border-yellow-600 bg-yellow-900';
      default: return 'border-blue-600 bg-blue-900';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const renderTabContent = () => {
    switch (selectedView) {
      case 'acquisition':
        return (
          <div className="space-y-6">
            {/* Customer Acquisition Trends */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <UserPlus className="w-5 h-5 text-blue-400" />
                <span>Customer Acquisition by Channel</span>
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={acquisitionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                  <Area type="monotone" dataKey="organic" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name="Organic Search" />
                  <Area type="monotone" dataKey="paid" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} name="Paid Search" />
                  <Area type="monotone" dataKey="referral" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.6} name="Referral" />
                  <Area type="monotone" dataKey="direct" stackId="1" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.6} name="Direct" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Customer Acquisition Cost */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                <span>Customer Acquisition Cost (CAC) Trend</span>
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={acquisitionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                  <Line type="monotone" dataKey="cac" stroke="#10B981" strokeWidth={3} name="CAC (£)" />
                  <Line type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={2} strokeDasharray="5 5" name="Total Acquisitions" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Channel Performance */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Target className="w-5 h-5 text-purple-400" />
                <span>Channel Performance</span>
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="pb-3 font-medium text-gray-400">Channel</th>
                      <th className="pb-3 font-medium text-gray-400">CAC (£)</th>
                      <th className="pb-3 font-medium text-gray-400">Volume</th>
                      <th className="pb-3 font-medium text-gray-400">Conversion Rate</th>
                      <th className="pb-3 font-medium text-gray-400">Efficiency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channelData.map((channel, index) => (
                      <tr key={index} className="border-b border-gray-700">
                        <td className="py-3 text-white">{channel.channel}</td>
                        <td className="py-3 text-white">£{channel.cac.toFixed(2)}</td>
                        <td className="py-3 text-white">{channel.volume}</td>
                        <td className="py-3 text-white">{channel.conversion}%</td>
                        <td className="py-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-16 bg-gray-600 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  channel.cac < 20 ? 'bg-green-500' : 
                                  channel.cac < 30 ? 'bg-yellow-500' : 'bg-red-500'
                                }`} 
                                style={{ width: `${Math.max(0, 100 - (channel.cac * 2))}%` }}
                              ></div>
                            </div>
                            <span className={`text-xs ${
                              channel.cac < 20 ? 'text-green-400' : 
                              channel.cac < 30 ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {channel.cac < 20 ? 'High' : channel.cac < 30 ? 'Medium' : 'Low'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Customer Journey Funnel */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Activity className="w-5 h-5 text-indigo-400" />
                <span>Customer Journey Funnel</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={journeyData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis type="number" stroke="#9CA3AF" />
                      <YAxis dataKey="name" type="category" stroke="#9CA3AF" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1F2937',
                          border: '1px solid #374151',
                          borderRadius: '8px',
                          color: '#F9FAFB'
                        }}
                      />
                      <Bar dataKey="value" fill="#4F46E5" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-4">
                  <h4 className="text-white font-medium">Stage Conversion Rates</h4>
                  {journeyData.map((stage, index) => (
                    index > 0 && (
                      <div key={index} className="bg-gray-700 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-300">{journeyData[index-1].name} → {stage.name}</span>
                          <span className="text-sm font-medium text-white">{stage.conversion}%</span>
                        </div>
                        <div className="w-full bg-gray-600 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              stage.conversion >= 70 ? 'bg-green-500' : 
                              stage.conversion >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                            }`} 
                            style={{ width: `${stage.conversion}%` }}
                          ></div>
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'feedback':
        return (
          <div className="space-y-6">
            {/* Sentiment Analysis */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Heart className="w-5 h-5 text-pink-400" />
                <span>Customer Sentiment Analysis</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-1">
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPieChart>
                      <Pie
                        data={sentimentData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ category, value }) => `${category}: ${value}%`}
                        labelLine={false}
                      >
                        {sentimentData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1F2937',
                          border: '1px solid #374151',
                          borderRadius: '8px',
                          color: '#F9FAFB'
                        }}
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="md:col-span-2">
                  <div className="grid grid-cols-3 gap-4 h-full">
                    <div className="bg-gray-700 rounded-lg p-4 flex flex-col items-center justify-center">
                      <Smile className="w-8 h-8 text-green-400 mb-2" />
                      <div className="text-2xl font-bold text-white">{sentimentData[0].value}%</div>
                      <div className="text-sm text-gray-400">Positive</div>
                    </div>
                    <div className="bg-gray-700 rounded-lg p-4 flex flex-col items-center justify-center">
                      <Meh className="w-8 h-8 text-gray-400 mb-2" />
                      <div className="text-2xl font-bold text-white">{sentimentData[1].value}%</div>
                      <div className="text-sm text-gray-400">Neutral</div>
                    </div>
                    <div className="bg-gray-700 rounded-lg p-4 flex flex-col items-center justify-center">
                      <Frown className="w-8 h-8 text-red-400 mb-2" />
                      <div className="text-2xl font-bold text-white">{sentimentData[2].value}%</div>
                      <div className="text-sm text-gray-400">Negative</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Feedback Themes */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <MessageCircle className="w-5 h-5 text-blue-400" />
                <span>Feedback Themes Analysis</span>
              </h3>
              <div className="space-y-4">
                {feedbackThemesData.map((theme, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-medium">{theme.theme}</h4>
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-1">
                          <ThumbsUp className="w-4 h-4 text-green-400" />
                          <span className="text-sm text-green-400">{theme.positive}%</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ThumbsDown className="w-4 h-4 text-red-400" />
                          <span className="text-sm text-red-400">{theme.negative}%</span>
                        </div>
                        <span className="text-sm text-gray-400">{theme.volume} mentions</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-3">
                      <div className="bg-green-500 h-3 rounded-l-full" style={{ width: `${theme.positive}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Complaint Tracking */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span>Complaint Tracking & Resolution</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={complaintData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="month" stroke="#9CA3AF" />
                      <YAxis stroke="#9CA3AF" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1F2937',
                          border: '1px solid #374151',
                          borderRadius: '8px',
                          color: '#F9FAFB'
                        }}
                      />
                      <Bar dataKey="volume" fill="#EF4444" name="Complaints" />
                      <Bar dataKey="resolved" fill="#10B981" name="Resolved" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={complaintData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="month" stroke="#9CA3AF" />
                      <YAxis stroke="#9CA3AF" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1F2937',
                          border: '1px solid #374151',
                          borderRadius: '8px',
                          color: '#F9FAFB'
                        }}
                      />
                      <Line type="monotone" dataKey="avgTime" stroke="#F59E0B" strokeWidth={3} name="Avg. Resolution Time (hours)" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <div className="text-sm text-gray-400 mb-1">Resolution Rate</div>
                  <div className="text-2xl font-bold text-green-400">97.1%</div>
                  <div className="text-xs text-gray-400">+2.3% vs last month</div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <div className="text-sm text-gray-400 mb-1">Avg. Resolution Time</div>
                  <div className="text-2xl font-bold text-white">5.8h</div>
                  <div className="text-xs text-green-400">-0.4h vs last month</div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <div className="text-sm text-gray-400 mb-1">First Response Time</div>
                  <div className="text-2xl font-bold text-white">1.2h</div>
                  <div className="text-xs text-green-400">-0.3h vs last month</div>
                </div>
              </div>
            </div>

            {/* NPS Breakdown */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Star className="w-5 h-5 text-yellow-400" />
                <span>NPS Breakdown</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <div className="text-sm text-gray-400 mb-1">Promoters</div>
                  <div className="text-2xl font-bold text-green-400">72%</div>
                  <div className="text-xs text-gray-400">Score 9-10</div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <div className="text-sm text-gray-400 mb-1">Passives</div>
                  <div className="text-2xl font-bold text-yellow-400">18%</div>
                  <div className="text-xs text-gray-400">Score 7-8</div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <div className="text-sm text-gray-400 mb-1">Detractors</div>
                  <div className="text-2xl font-bold text-red-400">10%</div>
                  <div className="text-xs text-gray-400">Score 0-6</div>
                </div>
              </div>
              <div className="mt-4 bg-gray-700 rounded-lg p-4">
                <h4 className="text-white font-medium mb-3">NPS Trend by Segment</h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-400">Enterprise</span>
                      <span className="text-sm text-white">78</span>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-400">SMB</span>
                      <span className="text-sm text-white">72</span>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '72%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-400">Consumer</span>
                      <span className="text-sm text-white">68</span>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '68%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'demographics':
        return (
          <div className="space-y-6">
            {/* Demographics Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Age Distribution */}
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  <span>Age Distribution</span>
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={demographicsData.age}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="name" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                    />
                    <Bar dataKey="value" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Gender Distribution */}
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  <span>Gender Distribution</span>
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <RechartsPieChart>
                    <Pie
                      data={demographicsData.gender}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}%`}
                      labelLine={false}
                    >
                      <Cell fill="#8B5CF6" />
                      <Cell fill="#EC4899" />
                      <Cell fill="#6B7280" />
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>

              {/* Geographic Distribution */}
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <MapPin className="w-5 h-5 text-green-400" />
                  <span>Geographic Distribution</span>
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <RechartsPieChart>
                    <Pie
                      data={demographicsData.location}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}%`}
                      labelLine={false}
                    >
                      <Cell fill="#10B981" />
                      <Cell fill="#3B82F6" />
                      <Cell fill="#F59E0B" />
                      <Cell fill="#EF4444" />
                      <Cell fill="#6B7280" />
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Customer Personas */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Users className="w-5 h-5 text-indigo-400" />
                <span>Customer Personas</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {personaData.map((persona, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: persona.color }}>
                        <span className="text-white font-bold">{persona.name.charAt(0)}</span>
                      </div>
                      <div>
                        <h4 className="text-white font-medium">{persona.name}</h4>
                        <span className="text-xs text-gray-400">{persona.percentage}% of customers</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-300 mb-3">{persona.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {persona.traits.map((trait, traitIndex) => (
                        <span key={traitIndex} className="px-2 py-1 bg-gray-600 text-gray-300 rounded-full text-xs">
                          {trait}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer Behavior Radar Chart */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Activity className="w-5 h-5 text-yellow-400" />
                <span>Customer Behavior Patterns</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart outerRadius={90} data={[
                      { subject: 'Mobile Usage', A: 85, B: 65, C: 45, fullMark: 100 },
                      { subject: 'Feature Adoption', A: 75, B: 80, C: 60, fullMark: 100 },
                      { subject: 'Session Length', A: 65, B: 85, C: 70, fullMark: 100 },
                      { subject: 'Purchase Frequency', A: 90, B: 70, C: 50, fullMark: 100 },
                      { subject: 'Support Usage', A: 55, B: 45, C: 80, fullMark: 100 },
                      { subject: 'Social Sharing', A: 70, B: 60, C: 40, fullMark: 100 },
                    ]}>
                      <PolarGrid stroke="#374151" />
                      <PolarAngleAxis dataKey="subject" stroke="#9CA3AF" />
                      <PolarRadiusAxis stroke="#9CA3AF" />
                      <Radar name="Enterprise Eva" dataKey="A" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} />
                      <Radar name="Startup Sam" dataKey="B" stroke="#10B981" fill="#10B981" fillOpacity={0.6} />
                      <Radar name="Technical Tina" dataKey="C" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.6} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1F2937',
                          border: '1px solid #374151',
                          borderRadius: '8px',
                          color: '#F9FAFB'
                        }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-4">
                  <h4 className="text-white font-medium">Behavioral Insights</h4>
                  <div className="bg-gray-700 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                      <div>
                        <div className="text-sm text-white font-medium">Enterprise Customers</div>
                        <p className="text-xs text-gray-400">High purchase frequency, low support needs, moderate mobile usage</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></div>
                      <div>
                        <div className="text-sm text-white font-medium">Startup Customers</div>
                        <p className="text-xs text-gray-400">High feature adoption, long sessions, moderate purchase frequency</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0"></div>
                      <div>
                        <div className="text-sm text-white font-medium">Technical Users</div>
                        <p className="text-xs text-gray-400">High support usage, moderate feature adoption, low social sharing</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'retention':
        return (
          <div className="space-y-6">
            {/* Retention & Churn Trends */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Activity className="w-5 h-5 text-green-400" />
                <span>Retention & Churn Trends</span>
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={retentionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                  <Area type="monotone" dataKey="retention" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name="Retention %" />
                  <Area type="monotone" dataKey="churn" stackId="1" stroke="#EF4444" fill="#EF4444" fillOpacity={0.6} name="Churn %" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Customer Lifetime Value */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-yellow-400" />
                <span>Customer Lifetime Value (CLTV)</span>
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={retentionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                  <Line type="monotone" dataKey="ltv" stroke="#F59E0B" strokeWidth={3} name="CLTV (£)" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Cohort Retention Analysis */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Users className="w-5 h-5 text-purple-400" />
                <span>Cohort Retention Analysis</span>
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="pb-3 font-medium text-gray-400">Cohort</th>
                      <th className="pb-3 font-medium text-gray-400">Month 1</th>
                      <th className="pb-3 font-medium text-gray-400">Month 2</th>
                      <th className="pb-3 font-medium text-gray-400">Month 3</th>
                      <th className="pb-3 font-medium text-gray-400">Month 4</th>
                      <th className="pb-3 font-medium text-gray-400">Month 5</th>
                      <th className="pb-3 font-medium text-gray-400">Month 6</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-700">
                      <td className="py-3 text-white">Jan 2024</td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">100%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">92%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">88%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">85%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">82%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">80%</div>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-700">
                      <td className="py-3 text-white">Feb 2024</td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">100%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">93%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">89%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">86%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">84%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-700">
                      <td className="py-3 text-white">Mar 2024</td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">100%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">91%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">87%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">85%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-700">
                      <td className="py-3 text-white">Apr 2024</td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">100%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">94%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">90%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-700">
                      <td className="py-3 text-white">May 2024</td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">100%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">95%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 text-white">Jun 2024</td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-green-900 text-green-300 rounded text-xs text-center">100%</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                      <td className="py-3">
                        <div className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs text-center">-</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Churn Reasons Analysis */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <UserMinus className="w-5 h-5 text-red-400" />
                <span>Churn Reasons Analysis</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={[
                      { reason: 'Price', percentage: 35 },
                      { reason: 'Missing Features', percentage: 28 },
                      { reason: 'Competitor', percentage: 18 },
                      { reason: 'Poor Support', percentage: 12 },
                      { reason: 'Other', percentage: 7 },
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="reason" stroke="#9CA3AF" />
                      <YAxis stroke="#9CA3AF" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1F2937',
                          border: '1px solid #374151',
                          borderRadius: '8px',
                          color: '#F9FAFB'
                        }}
                      />
                      <Bar dataKey="percentage" fill="#EF4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-4">
                  <h4 className="text-white font-medium">Churn Mitigation Strategies</h4>
                  <div className="bg-gray-700 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></div>
                      <div>
                        <div className="text-sm text-white font-medium">Price Sensitivity (35%)</div>
                        <p className="text-xs text-gray-400">Implement flexible pricing tiers and better communicate value proposition</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></div>
                      <div>
                        <div className="text-sm text-white font-medium">Missing Features (28%)</div>
                        <p className="text-xs text-gray-400">Accelerate product roadmap for most requested features and improve communication</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></div>
                      <div>
                        <div className="text-sm text-white font-medium">Competitor Offers (18%)</div>
                        <p className="text-xs text-gray-400">Implement competitive intelligence program and targeted retention offers</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'insights':
        return (
          <div className="space-y-6">
            {/* Customer Insights */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
                <span>Key Customer Insights</span>
              </h3>
              <div className="space-y-4">
                {customerInsights.map((insight) => (
                  <div key={insight.id} className={`p-4 rounded-lg border ${getInsightColor(insight.type)}`}>
                    <div className="flex items-start space-x-3">
                      {getInsightIcon(insight.type)}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white font-medium">{insight.title}</h4>
                          <span className="text-xs text-gray-400">{insight.timeframe}</span>
                        </div>
                        <p className="text-sm text-gray-300 mb-3">{insight.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <span className={`text-xs ${getImpactColor(insight.impact)} capitalize`}>
                              {insight.impact} impact
                            </span>
                            <span className="text-xs text-gray-400">
                              {insight.confidence}% confidence
                            </span>
                          </div>
                          <button className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours">
                            {insight.action}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer Segments Performance */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <PieChart className="w-5 h-5 text-blue-400" />
                <span>Customer Segments Performance</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">Enterprise Segment</h4>
                  <div className="grid grid-cols-2 gap-3 text-center mb-3">
                    <div>
                      <div className="text-xs text-gray-400">CLTV</div>
                      <div className="text-lg font-bold text-white">£680</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">Churn</div>
                      <div className="text-lg font-bold text-green-400">1.8%</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">NPS</div>
                      <div className="text-lg font-bold text-white">78</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">Growth</div>
                      <div className="text-lg font-bold text-green-400">+12%</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 mb-1">Key Insight:</div>
                  <p className="text-xs text-gray-300">Highest CLTV but showing early churn signals in specific verticals</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">SMB Segment</h4>
                  <div className="grid grid-cols-2 gap-3 text-center mb-3">
                    <div>
                      <div className="text-xs text-gray-400">CLTV</div>
                      <div className="text-lg font-bold text-white">£420</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">Churn</div>
                      <div className="text-lg font-bold text-yellow-400">2.4%</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">NPS</div>
                      <div className="text-lg font-bold text-white">72</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">Growth</div>
                      <div className="text-lg font-bold text-green-400">+18%</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 mb-1">Key Insight:</div>
                  <p className="text-xs text-gray-300">Fastest growing segment with strong feature adoption rates</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">Consumer Segment</h4>
                  <div className="grid grid-cols-2 gap-3 text-center mb-3">
                    <div>
                      <div className="text-xs text-gray-400">CLTV</div>
                      <div className="text-lg font-bold text-white">£180</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">Churn</div>
                      <div className="text-lg font-bold text-red-400">3.2%</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">NPS</div>
                      <div className="text-lg font-bold text-white">68</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">Growth</div>
                      <div className="text-lg font-bold text-green-400">+8%</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 mb-1">Key Insight:</div>
                  <p className="text-xs text-gray-300">Price sensitive but responds well to mobile-first features</p>
                </div>
              </div>
            </div>

            {/* Customer Feedback Word Cloud */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <MessageCircle className="w-5 h-5 text-purple-400" />
                <span>Voice of Customer Analysis</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">Top Positive Themes</h4>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-2 bg-green-900 text-green-300 rounded-lg text-sm">Ease of use</span>
                    <span className="px-3 py-2 bg-green-900 text-green-300 rounded-lg text-sm">Customer support</span>
                    <span className="px-3 py-2 bg-green-900 text-green-300 rounded-lg text-sm">Mobile app</span>
                    <span className="px-2 py-1 bg-green-900 text-green-300 rounded-lg text-xs">Reliability</span>
                    <span className="px-2 py-1 bg-green-900 text-green-300 rounded-lg text-xs">Integration</span>
                    <span className="px-2 py-1 bg-green-900 text-green-300 rounded-lg text-xs">Performance</span>
                    <span className="px-2 py-1 bg-green-900 text-green-300 rounded-lg text-xs">Features</span>
                    <span className="px-2 py-1 bg-green-900 text-green-300 rounded-lg text-xs">Intuitive</span>
                    <span className="px-2 py-1 bg-green-900 text-green-300 rounded-lg text-xs">Time-saving</span>
                    <span className="px-2 py-1 bg-green-900 text-green-300 rounded-lg text-xs">Value</span>
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">Top Improvement Areas</h4>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-2 bg-red-900 text-red-300 rounded-lg text-sm">Pricing</span>
                    <span className="px-3 py-2 bg-red-900 text-red-300 rounded-lg text-sm">Advanced features</span>
                    <span className="px-2 py-1 bg-red-900 text-red-300 rounded-lg text-xs">Documentation</span>
                    <span className="px-2 py-1 bg-red-900 text-red-300 rounded-lg text-xs">Learning curve</span>
                    <span className="px-2 py-1 bg-red-900 text-red-300 rounded-lg text-xs">Customization</span>
                    <span className="px-2 py-1 bg-red-900 text-red-300 rounded-lg text-xs">API limitations</span>
                    <span className="px-2 py-1 bg-red-900 text-red-300 rounded-lg text-xs">Reporting</span>
                    <span className="px-2 py-1 bg-red-900 text-red-300 rounded-lg text-xs">Onboarding</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 bg-gray-700 rounded-lg p-4">
                <h4 className="text-white font-medium mb-3">Recent Customer Verbatims</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <ThumbsUp className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-300 italic">"The mobile app has transformed how our team collaborates on the go. The recent UI improvements have made a huge difference."</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <ThumbsUp className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-300 italic">"Customer support team is exceptional - they resolved our issue within hours and followed up to ensure everything was working."</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <ThumbsDown className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-300 italic">"We love the product but the enterprise pricing tier is becoming difficult to justify to our finance team. More granular options would help."</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {customerMetrics.map((metric) => (
                <div 
                  key={metric.id} 
                  className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-gray-600 transition-colours"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-300">{metric.name}</h3>
                    <metric.icon className={`w-5 h-5 ${metric.positive ? 'text-green-500' : 'text-red-500'}`} />
                  </div>
                  <div className="flex items-baseline space-x-2 mb-2">
                    <span className="text-2xl font-bold text-white">{metric.unit === '£' ? '£' : ''}{metric.value}{metric.unit !== '£' ? metric.unit : ''}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${metric.positive ? 'text-green-500' : 'text-red-500'}`}>
                      {metric.positive ? '+' : ''}{metric.change}%
                    </span>
                    <span className="text-xs text-gray-500">Target: {metric.unit === '£' ? '£' : ''}{metric.target}{metric.unit !== '£' ? metric.unit : ''}</span>
                  </div>
                  <div className="mt-3 w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${metric.value >= metric.target ? 'bg-green-500' : 'bg-yellow-500'}`}
                      style={{ width: `${Math.min((metric.value / (metric.target * 1.2)) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Customer Acquisition & Retention */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Customer Acquisition */}
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                    <UserPlus className="w-5 h-5 text-blue-400" />
                    <span>Customer Acquisition</span>
                  </h3>
                  <button 
                    onClick={() => setSelectedView('acquisition')}
                    className="text-xs text-indigo-400 hover:text-indigo-300"
                  >
                    View Details →
                  </button>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={acquisitionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="month" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                    />
                    <Area type="monotone" dataKey="total" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} name="Total Acquisitions" />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="text-center">
                    <div className="text-xs text-gray-400">CAC</div>
                    <div className="text-lg font-bold text-white">£22.80</div>
                    <div className="text-xs text-green-400">-7.3%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-400">Conversion</div>
                    <div className="text-lg font-bold text-white">3.2%</div>
                    <div className="text-xs text-green-400">+0.4%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-400">Growth</div>
                    <div className="text-lg font-bold text-white">+18.5%</div>
                    <div className="text-xs text-green-400">vs target</div>
                  </div>
                </div>
              </div>

              {/* Customer Retention */}
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                    <Users className="w-5 h-5 text-green-400" />
                    <span>Customer Retention</span>
                  </h3>
                  <button 
                    onClick={() => setSelectedView('retention')}
                    className="text-xs text-indigo-400 hover:text-indigo-300"
                  >
                    View Details →
                  </button>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={retentionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="month" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                    />
                    <Line type="monotone" dataKey="retention" stroke="#10B981" strokeWidth={3} name="Retention %" />
                    <Line type="monotone" dataKey="churn" stroke="#EF4444" strokeWidth={2} strokeDasharray="5 5" name="Churn %" />
                  </LineChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="text-center">
                    <div className="text-xs text-gray-400">Retention</div>
                    <div className="text-lg font-bold text-white">96%</div>
                    <div className="text-xs text-green-400">+1%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-400">Churn</div>
                    <div className="text-lg font-bold text-white">4%</div>
                    <div className="text-xs text-green-400">-1%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-400">CLTV</div>
                    <div className="text-lg font-bold text-white">£420</div>
                    <div className="text-xs text-green-400">+£10</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Feedback & Demographics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Customer Feedback */}
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                    <MessageCircle className="w-5 h-5 text-purple-400" />
                    <span>Customer Feedback</span>
                  </h3>
                  <button 
                    onClick={() => setSelectedView('feedback')}
                    className="text-xs text-indigo-400 hover:text-indigo-300"
                  >
                    View Details →
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-700 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-400 mb-1">NPS Score</div>
                    <div className="text-xl font-bold text-white">72</div>
                    <div className="text-xs text-green-400">+8 vs last quarter</div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-400 mb-1">CSAT Score</div>
                    <div className="text-xl font-bold text-white">4.7/5</div>
                    <div className="text-xs text-green-400">+0.3 vs last quarter</div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-400 mb-1">Sentiment</div>
                    <div className="text-xl font-bold text-green-400">65% Positive</div>
                    <div className="text-xs text-green-400">+5% vs last quarter</div>
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-3">
                  <h4 className="text-white font-medium mb-2">Top Feedback Themes</h4>
                  <div className="space-y-2">
                    {feedbackThemesData.slice(0, 3).map((theme, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm text-gray-300">{theme.theme}</span>
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-1">
                            <ThumbsUp className="w-3 h-3 text-green-400" />
                            <span className="text-xs text-green-400">{theme.positive}%</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <ThumbsDown className="w-3 h-3 text-red-400" />
                            <span className="text-xs text-red-400">{theme.negative}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Customer Demographics */}
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                    <Users className="w-5 h-5 text-yellow-400" />
                    <span>Customer Demographics</span>
                  </h3>
                  <button 
                    onClick={() => setSelectedView('demographics')}
                    className="text-xs text-indigo-400 hover:text-indigo-300"
                  >
                    View Details →
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-700 rounded-lg p-3">
                    <h4 className="text-white font-medium mb-2">Age Distribution</h4>
                    <div className="space-y-2">
                      {demographicsData.age.map((age, index) => (
                        <div key={index}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-400">{age.name}</span>
                            <span className="text-white">{age.value}%</span>
                          </div>
                          <div className="w-full bg-gray-600 rounded-full h-2">
                            <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${age.value}%` }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-3">
                    <h4 className="text-white font-medium mb-2">Geographic Distribution</h4>
                    <div className="space-y-2">
                      {demographicsData.location.map((location, index) => (
                        <div key={index}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-400">{location.name}</span>
                            <span className="text-white">{location.value}%</span>
                          </div>
                          <div className="w-full bg-gray-600 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{ width: `${location.value}%` }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-4 bg-gray-700 rounded-lg p-3">
                  <h4 className="text-white font-medium mb-2">Customer Personas</h4>
                  <div className="flex flex-wrap gap-2">
                    {personaData.map((persona, index) => (
                      <div key={index} className="flex items-center space-x-1 px-2 py-1 bg-gray-600 rounded-full">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: persona.color }}></div>
                        <span className="text-xs text-white">{persona.name}</span>
                        <span className="text-xs text-gray-400">{persona.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Insights */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                  <Lightbulb className="w-5 h-5 text-indigo-400" />
                  <span>AI-Generated Customer Insights</span>
                </h3>
                <button 
                  onClick={() => setSelectedView('insights')}
                  className="text-xs text-indigo-400 hover:text-indigo-300"
                >
                  View All Insights →
                </button>
              </div>
              <div className="space-y-4">
                {customerInsights.map((insight) => (
                  <div key={insight.id} className={`p-4 rounded-lg border ${getInsightColor(insight.type)}`}>
                    <div className="flex items-start space-x-3">
                      {getInsightIcon(insight.type)}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white font-medium">{insight.title}</h4>
                          <span className="text-xs text-gray-400">{insight.timeframe}</span>
                        </div>
                        <p className="text-sm text-gray-300 mb-3">{insight.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <span className={`text-xs ${getImpactColor(insight.impact)} capitalize`}>
                              {insight.impact} impact
                            </span>
                            <span className="text-xs text-gray-400">
                              {insight.confidence}% confidence
                            </span>
                          </div>
                          <button className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours">
                            {insight.action}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center space-x-3">
            <Users className="w-8 h-8 text-blue-400" />
            <span>Customer Dashboard</span>
          </h1>
          <p className="text-gray-400">Comprehensive customer insights, acquisition metrics, and feedback analysis</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none"
          >
            <option value="weekly">Weekly View</option>
            <option value="monthly">Monthly View</option>
            <option value="quarterly">Quarterly View</option>
          </select>
          <select
            value={selectedSegment}
            onChange={(e) => setSelectedSegment(e.target.value)}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none"
          >
            <option value="all">All Segments</option>
            <option value="enterprise">Enterprise</option>
            <option value="smb">SMB</option>
            <option value="consumer">Consumer</option>
          </select>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-700">
        <nav className="flex space-x-8 overflow-x-auto">
          {[
            { id: 'overview', name: 'Overview', icon: BarChart3 },
            { id: 'acquisition', name: 'Acquisition', icon: UserPlus },
            { id: 'retention', name: 'Retention & Loyalty', icon: Heart },
            { id: 'feedback', name: 'Feedback & Sentiment', icon: MessageCircle },
            { id: 'demographics', name: 'Demographics & Personas', icon: Users },
            { id: 'insights', name: 'Insights & Recommendations', icon: Lightbulb }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedView(tab.id)}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                selectedView === tab.id
                  ? 'border-indigo-500 text-indigo-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default CustomerDashboard;